import { useState } from 'react';
import type { MenuProps } from 'antd';
import { Button, Dropdown } from 'antd';
import { useNavigate } from 'react-router-dom';
import { IoFlashOutline } from 'react-icons/io5';
import { FiCheckSquare, FiChevronDown, FiEdit } from 'react-icons/fi';
import { CheckSquareOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  approveDeleteRegulation,
  rejectDeleteRegulation,
  requestDeleteRegulation,
} from '../../Regulation.services.tsx';
import { useRegulation } from '../../useRegulation';
import { MoreInfo } from '../more-info';
import { ConfirmModal } from '../../../../components/confirm-modal';
import { usePermissions } from '../../../../hooks/usePermissions.ts';
import { useAuth } from '../../../../context/AuthContext.tsx';

const RegulationsActionsBar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showConfirmDeletion, setShowConfirmDeletion] = useState(false);
  const [showApproveDeletion, setShowApproveDeletion] = useState(false);
  const [showRejectDeletion, setShowRejectDeletion] = useState(false);
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const { user } = useAuth();

  const isRequest = location.pathname.includes('request-deletion');
  const handleShowMoreInfo = () => setShowMoreInfo(true);
  const handleCloseMoreInfo = () => setShowMoreInfo(false);
  const queryClient = useQueryClient();
  const { isAdmin, isRegularUser } = usePermissions();

  const { id, data } = useRegulation();
  const { isAllowed } = usePermissions(data?.type);
  const handleMenuClick = (item: { key: string }) => {
    switch (item.key) {
      case '1': {
        navigate(`/regulation/draft/${data?.regulationDraftId}`);
        break;
      }
      case '2': {
        setShowConfirmDeletion(true);
        break;
      }
      case '3': {
        const recipients = Array.isArray(data.forwardEmails)
          ? data.forwardEmails.join(',')
          : String(data.forwardEmails);

        const subject = encodeURIComponent(data.title || 'Regulation Draft');
        const url = `${window.location.origin}/regulation/draft/view/${data.id}`;
        const body = encodeURIComponent(`Link to the regulation:\n${url}`);
        window.location.href = `mailto:${recipients}?subject=${subject}&body=${body}`;
        break;
      }
      case '4': {
        setShowApproveDeletion(true);
        break;
      }
      case '5': {
        rejectDeleteMutation.mutate({ id });
        setShowConfirmDeletion(false);
        break;
      }
      default: {
        break;
      }
    }
  };

  const isInLeadership = data?.unitInCharge?.leadership?.includes(user?.id);

  const canEdit =
    (!isRequest && id && isRegularUser && !data?.isDeleted && data?.isActive) ||
    (!isRequest && id && isAdmin && !data?.isDeleted && data?.isActive) ||
    (isRequest && id && isAllowed && !data?.isDeleted && data?.isActive);
  const canDelete =
    (!isRequest && id && isRegularUser && !data?.isDeleted && data?.isActive) ||
    (!isRequest && id && isAdmin && !data?.isDeleted && data?.isActive);
  const canSendViaEmail =
    isRequest && data?.forwardEmails && !isAllowed && !data?.isDeleted && data?.isActive;
  const canApproveDeletion =
    (id && isRequest && isAllowed && !data?.isDeleted && data?.isActive) ||
    (id && isRequest && isAdmin && !data?.isDeleted && data?.isActive) ||
    (id && isRequest && isInLeadership && data?.type === 3 && !data?.isDeleted && data?.isActive);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const items: MenuProps['items'] = [
    ...(canEdit
      ? [
          {
            label: t('common.edit'),
            key: '1',
            icon: <FiEdit />,
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            label: t('common.delete-regulation'),
            key: '2',
            icon: <FiCheckSquare />,
          },
        ]
      : []),
    ...(canSendViaEmail
      ? [
          {
            label: t('common.forward'),
            key: '3',
            icon: <CheckSquareOutlined />,
          },
        ]
      : []),

    ...(canApproveDeletion
      ? [
          {
            key: '0001',
            type: 'group',
            label: t('common.deletion-request'),
            children: [
              {
                label: t('common.confirm'),
                key: '4',
                icon: <CheckSquareOutlined />,
              },
              {
                label: t('common.decline'),
                key: '5',
                icon: <CheckSquareOutlined />,
              },
            ],
          },
        ]
      : []),
  ];

  const requestDeleteMutation = useMutation({
    mutationFn: requestDeleteRegulation,
    onSuccess: () => {
      navigate('/');
      queryClient.invalidateQueries({ queryKey: ['Regulations'] });
    },
    onError: () => {
      console.error('Failed to request del;etion');
    },
  });

  const approveDeleteMutation = useMutation({
    mutationFn: approveDeleteRegulation,
    onSuccess: () => {
      navigate(-1);
      queryClient.invalidateQueries({ queryKey: ['Regulations'] });
    },
    onError: () => {
      console.error('Failed to request del;etion');
    },
  });

  const rejectDeleteMutation = useMutation({
    mutationFn: rejectDeleteRegulation,
    onSuccess: () => {
      navigate(-1);
      queryClient.invalidateQueries({ queryKey: ['Regulations'] });
    },
    onError: () => {
      console.error('Failed to request del;etion');
    },
  });

  const handleCloseConfirmDeletion = () => setShowConfirmDeletion(false);
  const handleCloseApproveDeletion = () => setShowApproveDeletion(false);
  const handleCloseRejectDeletion = () => setShowRejectDeletion(false);

  const handleConfirmDeletion = (message: string) => {
    requestDeleteMutation.mutate({ id, data: { deletionMessage: message } });
    setShowConfirmDeletion(false);
  };

  const handleApproveDeletion = (
    message: string,
    subject: string,
    groups: number[],
    notifications: boolean,
  ) => {
    if (!id) return;
    approveDeleteMutation.mutate({
      id,
      data: {
        requestMessage: message,
        title: notifications ? subject : null,
        distributionLists: notifications ? groups.map((item) => Number(item)) : null,
      },
    });
    setShowApproveDeletion(false);
  };

  const handleRejectDeletion = () => {
    rejectDeleteMutation.mutate({ id });
    setShowConfirmDeletion(false);
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };
  return (
    <>
      <div className="action-bar-container">
        <div className="action-bar-left">
          <Button
            size="large"
            onClick={handleShowMoreInfo}
            icon={<IoFlashOutline />}
            iconPosition="start"
          >
            {t('common.more')}
            <FiChevronDown />
          </Button>
        </div>
        <div className="action-bar-right">
          <Dropdown menu={menuProps}>
            <Button size="large" type="primary" icon={<IoFlashOutline />} iconPosition="start">
              {t('common.actions')}
              <FiChevronDown />
            </Button>
          </Dropdown>
        </div>
      </div>
      {showMoreInfo && <MoreInfo open={showMoreInfo} onClose={handleCloseMoreInfo} />}
      {showConfirmDeletion && (
        <ConfirmModal
          open={showConfirmDeletion}
          title="Request Deletion"
          simple
          message="Here you can enter a message that will be sent with the deletion."
          onClose={handleCloseConfirmDeletion}
          onConfirm={handleConfirmDeletion}
        />
      )}
      {showRejectDeletion && (
        <ConfirmModal
          open={showRejectDeletion}
          title="Reject Deletion"
          simple
          message="Here you can enter a message that will be sent with the reject."
          onClose={handleCloseRejectDeletion}
          onConfirm={handleRejectDeletion}
        />
      )}
      {showApproveDeletion && (
        <ConfirmModal
          open={showApproveDeletion}
          title="Approve Deletion"
          subject={data?.title}
          message="Here you can enter a message that will be sent with the deletion. Various distribution lists are available. You can select multiple distribution lists."
          onClose={handleCloseApproveDeletion}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          onConfirm={handleApproveDeletion}
        />
      )}
    </>
  );
};

export default RegulationsActionsBar;